"use client";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const isCoverageBased = (uom = "") =>
  ["SF", "LF"].some((u) => uom.trim().toUpperCase().includes(u));

const calcPackageCost = (ratePerUnit, coverage) =>
  parseFloat(ratePerUnit || 0) * (parseFloat(coverage) || 1);

// ── Dimension parser (same as MaterialsTab3) ──────────────────────────────
function tokenToFeet(s) {
  s = (s || "").trim();
  if (!s) return null;
  let m = s.match(
    /^(\d+(?:\.\d+)?)'[\s]*(\d+(?:-\d+\/\d+)?(?:\/\d+)?|\d*-\d+\/\d+)"$/,
  );
  if (m) return parseFloat(m[1]) + inchStrToFeet(m[2]);
  m = s.match(/^(\d+(?:\.\d+)?)'$/);
  if (m) return parseFloat(m[1]);
  m = s.match(/^(\d+(?:-\d+\/\d+)?(?:\/\d+)?)"$/);
  if (m) return inchStrToFeet(m[1]);
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
}

function inchStrToFeet(s) {
  s = (s || "").trim();
  let m = s.match(/^(\d+)-(\d+)\/(\d+)$/);
  if (m) return (parseInt(m[1]) + parseInt(m[2]) / parseInt(m[3])) / 12;
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1]) / parseInt(m[2]) / 12;
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]) / 12;
  return 0;
}

function parseSizeCoverage(size, uom) {
  if (!size || !uom) return null;
  const u = uom.trim().toUpperCase();
  const s = size.trim();
  if (u.includes("SF")) {
    const parts = s.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const a = tokenToFeet(parts[0].trim());
      const b = tokenToFeet(parts[1].trim());
      if (a !== null && b !== null) return Math.round(a * b * 10000) / 10000;
    }
    return null;
  }
  if (u.includes("LF")) {
    const ft = tokenToFeet(s);
    return ft !== null ? Math.round(ft * 10000) / 10000 : null;
  }
  return null;
}

// ── Item Search Modal ─────────────────────────────────────────────────────
function ItemSearchModal({ onSelect, onClose, token }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(
    async (q) => {
      if (!q.trim()) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/item-database?search=${encodeURIComponent(q)}&limit=50&category=M`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setItems(res.data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h3 className="text-base font-semibold text-white">
            Select Item from Database
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-3">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by item code or description…"
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
          />
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-1">
          {loading && (
            <p className="text-gray-400 text-sm py-4 text-center">Searching…</p>
          )}
          {!loading && items.length === 0 && query && (
            <p className="text-gray-500 text-sm py-4 text-center">
              No items found.
            </p>
          )}
          {items.map((item) => {
            const coverage = parseFloat(item.coveragePerUnit) || 1;
            const covered = isCoverageBased(item.uom);
            const pkgCost = calcPackageCost(
              item.defaultCost,
              covered ? coverage : 1,
            );
            return (
              <button
                key={item._id}
                onClick={() => onSelect(item)}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-orange-400">
                    {item.itemCode}
                  </span>
                  <span className="text-xs text-gray-500">
                    {covered
                      ? `$${fmt(item.defaultCost)}/${item.uom} → $${fmt(pkgCost)}/PC`
                      : `$${fmt(item.defaultCost)}/${item.uom || "EA"}`}
                  </span>
                </div>
                <p className="text-sm text-gray-200 mt-0.5">
                  {item.description}
                </p>
                <div className="flex items-center gap-3 mt-0.5">
                  {item.size && (
                    <span className="text-xs text-gray-400">
                      Size: {item.size}
                    </span>
                  )}
                  {covered && (
                    <span className="text-xs text-blue-400">
                      {coverage} {item.uom}/PC
                    </span>
                  )}
                  {item.defaultVendor?.companyName && (
                    <span className="text-xs text-gray-500">
                      Default: {item.defaultVendor.companyName}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Line Item Row ─────────────────────────────────────────────────────────
function LineItemRow({ item, idx, vendors, onUpdate, onRemove }) {
  const updateItem = (field, value) => onUpdate(idx, field, value);

  return (
    <div className="bg-gray-700 rounded-xl border border-gray-600 p-3 space-y-2.5">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono text-orange-400">
            {item.itemCode}
          </p>
          <p className="text-sm text-gray-200 font-medium leading-snug">
            {item.description}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {item.isCoverage
              ? `Coverage-based · ordered by PC · priced per ${item.uom}`
              : "Per unit"}
          </p>
        </div>
        <button
          onClick={() => onRemove(idx)}
          className="text-gray-500 hover:text-red-400 text-xl leading-none shrink-0"
        >
          ×
        </button>
      </div>

      {/* Coverage equation pill */}
      {item.isCoverage && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 bg-blue-900/30 border border-blue-700/40 rounded-lg text-xs text-blue-300 font-mono">
          <span className="font-semibold">
            {parseFloat(item.coveragePerUnit) || 1} {item.uom}/PC
          </span>
          <span className="text-blue-500">×</span>
          <span>
            ${fmt(item.ratePerUnit)}/{item.uom}
          </span>
          <span className="text-blue-500">=</span>
          <span className="font-bold text-blue-200">
            ${fmt(item.unitPrice)}/PC
          </span>
        </div>
      )}

      {/* Row 1: Size · Coverage · Rate · Package cost */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            {item.isCoverage ? "Size (auto-calculates coverage)" : "Size"}
          </label>
          <input
            type="text"
            value={item.size}
            placeholder={
              item.isCoverage
                ? item.uom?.toUpperCase().includes("SF")
                  ? "e.g. 4'x8'"
                  : "e.g. 8'"
                : ""
            }
            onChange={(e) => updateItem("size", e.target.value)}
            className={cls(
              "w-full px-2 py-1.5 text-xs rounded-lg text-gray-200 placeholder-gray-600 focus:outline-none",
              item.isCoverage
                ? parseSizeCoverage(item.size, item.uom) !== null
                  ? "bg-gray-600 border border-green-600/50 focus:border-orange-500"
                  : "bg-gray-600 border border-orange-600/50 focus:border-orange-500"
                : "bg-gray-600 border border-gray-500 focus:border-orange-500",
            )}
          />
        </div>

        {/* Coverage */}
        {item.isCoverage ? (
          <div>
            <label className="text-[10px] text-gray-400 mb-0.5 block">
              Coverage ({item.uom}/PC) — auto
            </label>
            <div
              className={cls(
                "px-2 py-1.5 text-xs rounded-lg font-mono font-semibold border",
                parseSizeCoverage(item.size, item.uom) !== null
                  ? "bg-blue-900/30 border-blue-700/40 text-blue-200"
                  : "bg-gray-600/50 border-gray-600 text-gray-400",
              )}
            >
              {parseSizeCoverage(item.size, item.uom) !== null
                ? `${item.coveragePerUnit} ${item.uom}`
                : "Enter size above"}
            </div>
          </div>
        ) : (
          <div>
            <label className="text-[10px] text-gray-400 mb-0.5 block">
              Coverage
            </label>
            <div className="px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-gray-500">
              1 (fixed)
            </div>
          </div>
        )}

        {/* Rate */}
        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            {item.isCoverage ? `Rate (/${item.uom})` : "Unit Price"}
          </label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
              $
            </span>
            <input
              type="number"
              value={item.ratePerUnit}
              min="0"
              step="0.01"
              onChange={(e) => updateItem("ratePerUnit", e.target.value)}
              className="w-full pl-5 pr-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Package cost */}
        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            {item.isCoverage ? "Package Cost ($/PC)" : "Unit Price ($/PC)"}
          </label>
          {item.isCoverage ? (
            <div className="px-2 py-1.5 text-xs bg-gray-600/50 border border-blue-700/40 rounded-lg text-blue-200 font-mono font-semibold">
              ${fmt(item.unitPrice)}
            </div>
          ) : (
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                $
              </span>
              <input
                type="number"
                value={item.unitPrice}
                min="0"
                step="0.01"
                onChange={(e) => updateItem("unitPrice", e.target.value)}
                className="w-full pl-5 pr-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Qty · Section · Vendor · Line total */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            Qty (PC)
          </label>
          <input
            type="number"
            value={item.quantity}
            min="0"
            onChange={(e) => updateItem("quantity", e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            Section #
          </label>
          <input
            type="text"
            value={item.sectionNumber}
            placeholder="e.g. 01"
            onChange={(e) => updateItem("sectionNumber", e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            Vendor
          </label>
          <select
            value={item.vendor}
            onChange={(e) => updateItem("vendor", e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          >
            <option value="">Unassigned</option>
            {vendors.map((v) => (
              <option key={v._id} value={v._id}>
                {v.companyName}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col justify-end">
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            Line Total
          </label>
          <div className="px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-green-300 font-mono font-semibold text-right">
            $
            {fmt(
              (parseFloat(item.quantity) || 0) *
                (parseFloat(item.unitPrice) || 0),
            )}
          </div>
        </div>
      </div>

      <input
        type="text"
        value={item.notes}
        placeholder="Item notes…"
        onChange={(e) => updateItem("notes", e.target.value)}
        className="w-full px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-gray-400 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function CreatePO3() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // Header fields
  const [title, setTitle] = useState("");
  const [bidArea, setBidArea] = useState("");
  const [notes, setNotes] = useState("");

  // Items
  const [items, setItems] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Vendors & existing floors (for autocomplete suggestions)
  const [vendors, setVendors] = useState([]);
  const [existingFloors, setExistingFloors] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, poRes] = await Promise.all([
          axios.get(`${API}/accounts`, { headers }),
          axios.get(`${API}/purchaseOrders3/project/${projectId}`, { headers }),
        ]);
        setVendors((vRes.data || []).filter((v) => v.type === "vendor"));
        // Collect unique bidAreas already on the project for suggestions
        const floors = [
          ...new Set(
            (poRes.data.purchaseOrders || [])
              .map((p) => p.bidArea)
              .filter(Boolean),
          ),
        ].sort();
        setExistingFloors(floors);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [projectId, headers]);

  // Add item from DB
  const addItem = (dbItem) => {
    const uom = dbItem.uom || "EA";
    const covered = isCoverageBased(uom);
    const size = dbItem.size || "";
    const parsedCoverage = covered ? parseSizeCoverage(size, uom) : null;
    const coverage =
      parsedCoverage !== null
        ? parsedCoverage
        : covered
          ? parseFloat(dbItem.coveragePerUnit) || 1
          : 1;
    let vendorId = dbItem.defaultVendor?._id || dbItem.defaultVendor || "";
    let rate = parseFloat(dbItem.defaultCost) || 0;
    if (vendorId && dbItem.vendorPrices?.length) {
      const vp = dbItem.vendorPrices.find(
        (v) => v.vendor?.toString() === vendorId.toString(),
      );
      if (vp) rate = vp.price || vp.unitRate || rate;
    }
    setItems((prev) => [
      ...prev,
      {
        itemDatabaseId: dbItem._id,
        itemCode: dbItem.itemCode || "",
        description: dbItem.description || "",
        size,
        uom,
        isCoverage: covered,
        coveragePerUnit: coverage,
        ratePerUnit: rate,
        unitPrice: calcPackageCost(rate, coverage),
        vendor: vendorId,
        quantity: 1,
        sectionNumber: "",
        notes: "",
        vendorPrices: dbItem.vendorPrices || [],
      },
    ]);
    setShowSearch(false);
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const next = { ...item, [field]: value };

        if (field === "size" && next.isCoverage) {
          const parsed = parseSizeCoverage(value, next.uom);
          if (parsed !== null) {
            next.coveragePerUnit = parsed;
            next.unitPrice = calcPackageCost(next.ratePerUnit, parsed);
          }
        }
        if (field === "ratePerUnit" || field === "coveragePerUnit") {
          next.unitPrice = next.isCoverage
            ? calcPackageCost(next.ratePerUnit, next.coveragePerUnit)
            : parseFloat(next.ratePerUnit || 0);
        }
        if (field === "vendor" && value && next.vendorPrices?.length) {
          const vp = next.vendorPrices.find(
            (v) => (v.vendor?._id || v.vendor)?.toString() === value.toString(),
          );
          if (vp) {
            const r = vp.price || vp.unitRate || 0;
            next.ratePerUnit = r;
            next.unitPrice = next.isCoverage
              ? calcPackageCost(r, next.coveragePerUnit)
              : r;
          }
        }
        return next;
      }),
    );
  };

  const removeItem = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const grandTotal = useMemo(
    () =>
      items.reduce(
        (s, i) =>
          s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unitPrice) || 0),
        0,
      ),
    [items],
  );

  const handleSave = async () => {
    if (!title.trim()) return setError("Title is required.");
    if (!bidArea.trim()) return setError("Floor / Bid Area is required.");
    if (!items.length) return setError("Add at least one item.");

    setSaving(true);
    setError("");
    try {
      const payload = {
        title: title.trim(),
        bidArea: bidArea.trim(),
        sectionNumbers: [
          ...new Set(items.map((i) => i.sectionNumber).filter(Boolean)),
        ],
        sectionNumber: items[0]?.sectionNumber || "",
        notes,
        lineItems: items.map((item) => ({
          itemDatabaseId: item.itemDatabaseId,
          itemCode: item.itemCode,
          description: item.description,
          itemDescription: item.description,
          size: item.size,
          uom: item.uom,
          container: item.uom,
          coveragePerUnit: parseFloat(item.coveragePerUnit) || 1,
          quantity: parseFloat(item.quantity) || 0,
          unitCost: parseFloat(item.unitPrice) || 0,
          total:
            (parseFloat(item.quantity) || 0) *
            (parseFloat(item.unitPrice) || 0),
          vendor: item.vendor || null,
          sectionNumber: item.sectionNumber || "",
          bidArea: bidArea.trim(),
          estimatedQty: parseFloat(item.quantity) || 0,
          estimatedTotal:
            (parseFloat(item.quantity) || 0) *
            (parseFloat(item.unitPrice) || 0),
          source: "manual",
          notes: item.notes,
        })),
      };

      await axios.post(`${API}/purchaseOrders3/project/${projectId}`, payload, {
        headers,
      });
      navigate(`/Projects/${projectId}?tab=Purchase Orders`);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {showSearch && (
        <ItemSearchModal
          token={token}
          onSelect={addItem}
          onClose={() => setShowSearch(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/Projects/${projectId}?tab=Purchase Orders`)}
          className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <div>
          <h1 className="text-lg font-bold text-white">New Purchase Order</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Manual PO — items added from the item database
          </p>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-900/50 border border-red-700 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}

      {/* PO header fields */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-white">PO Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Floor 3 — Drywall Materials"
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">
              Floor / Bid Area *
              {existingFloors.length > 0 && (
                <span className="ml-2 text-gray-500">— or pick existing:</span>
              )}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={bidArea}
                onChange={(e) => setBidArea(e.target.value)}
                placeholder="e.g. Floor 3"
                className="flex-1 px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              />
              {existingFloors.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) setBidArea(e.target.value);
                  }}
                  className="px-2 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-400 focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Existing…</option>
                  {existingFloors.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              Items on the same floor will be visible together in the PO detail
              view.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Internal notes…"
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
          />
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">
            Line Items
            {items.length > 0 && (
              <span className="ml-2 text-xs text-gray-500 font-normal">
                {items.length} item{items.length !== 1 ? "s" : ""} ·{" "}
                <span className="text-green-300">${fmt(grandTotal)}</span>
              </span>
            )}
          </h2>
          <button
            onClick={() => setShowSearch(true)}
            className="px-4 py-2 text-sm font-semibold bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            + Add Item
          </button>
        </div>

        {!items.length && (
          <div className="border border-dashed border-gray-600 rounded-xl py-10 text-center">
            <p className="text-sm text-gray-500 mb-3">No items yet.</p>
            <button
              onClick={() => setShowSearch(true)}
              className="px-4 py-2 text-sm font-semibold bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Search Item Database
            </button>
          </div>
        )}

        {items.map((item, idx) => (
          <LineItemRow
            key={idx}
            item={item}
            idx={idx}
            vendors={vendors}
            onUpdate={updateItem}
            onRemove={removeItem}
          />
        ))}

        {/* Grand total bar */}
        {items.length > 0 && (
          <div className="flex items-center justify-between bg-gray-800 rounded-xl border border-gray-700 px-5 py-3">
            <span className="text-sm text-gray-400">{items.length} items</span>
            <div className="text-right">
              <p className="text-xs text-gray-500">Grand Total</p>
              <p className="text-xl font-bold text-green-300 font-mono">
                ${fmt(grandTotal)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Save / Cancel */}
      <div className="flex gap-3 pt-2 border-t border-gray-700">
        <button
          onClick={handleSave}
          disabled={saving}
          className={cls(
            "px-6 py-2.5 text-sm font-semibold rounded-lg text-white transition-colors",
            saving
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600",
          )}
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full" />
              Creating PO…
            </span>
          ) : (
            "Create Purchase Order"
          )}
        </button>
        <button
          onClick={() => navigate(`/Projects/${projectId}?tab=Purchase Orders`)}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-600 hover:border-gray-400 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
